import {browserHistory} from 'react-router';
import AuthStore from 'stores/AuthStore';
import AuthActions from 'actions/AuthActions';
import {AuthSource} from 'stores/AuthSource';
import {DashUserParams, getDashboardPath} from 'components/StatsPage2.utils';
import {RouterOnChange, RouterOnEnter, RouterState} from '@store/@types/util/ReactRouter';
import {UserId} from 'records/User';
import {isEmbedPath} from 'utils/mediafly/isEmbedded';
import {getQueryParam} from 'utils/getQueryParam';
import hasFlag from 'utils/hasFlag';
import {featureFlags} from 'utils/const';

const embedPath = '/embed';
const gumIdpName = 'mediafly';

/**
 * Maintain embed state when navigating
 */
export function embeddedReplace(path) {
  if (path.startsWith(embedPath)) {
    browserHistory.replace(path);
  }
  if (isEmbedPath()) {
    browserHistory.replace(embedPath + path);
  } else {
    browserHistory.replace(path);
  }
}

/**
 * Redirect to login when trying to enter route, saving the attempted path
 * so we can proceed after login.
 */
function redirectToLogin(nextState) {
  const nextPathname = nextState.location.pathname + nextState.location.search;
  AuthActions.saveNext(nextPathname);
  browserHistory.replace({pathname: '/login'});
}

export function redirectToSamlLogin(nextState: string | null, idpName, tenantId) {
  window.location.replace(
    `/api/login/saml/initiate?name=${idpName}${
      !!tenantId ? `&tenantId=${tenantId}` : ''
    }&redirectPath=${encodeURIComponent(nextState ?? '')}`
  );
}

/**
 * redirect to '/' and then let RoleBasedDynamicIndex do the rest (a second redirect,
 * so not ideal, but we need to access an alt store so would have to go through it)
 */
function redirectToHome() {
  browserHistory.replace('/');
}

export const authCheck: RouterOnEnter = async (nextState, _, callback) => {
  AuthSource.checkSession()
    .then(hasSession => {
      if (hasSession) {
        const user = AuthStore.getState().user;
        return user ?? AuthActions.getUserMeInfo();
      }

      const tenantId = getQueryParam(nextState.location, 'tenantId');
      const isChromium = navigator.userAgent.includes('Chrome');

      // Only use saml login for chromium browsers with tenant id
      if (!!tenantId && isChromium) {
        const nextStatePath = nextState.location.pathname + nextState.location.search;
        redirectToSamlLogin(nextStatePath, gumIdpName, tenantId);
      } else {
        redirectToLogin(nextState);
      }
    })
    .finally(() => {
      callback();
    });
};

/**
 * Auth check for routes coming from salesforce
 */
export const salesforceAuthCheck: RouterOnEnter = nextState => {
  const {user} = AuthStore.getState();
  if (!user) {
    redirectToLogin(nextState);
  } else {
    redirectToHome();
  }
};

export const adminHomeRedirectEnter: RouterOnEnter = (nextState, replace) => {
  adminHomeRedirectChange(nextState, nextState, replace);
};

/**
 * Determine which route to show for /admin
 */
export const adminHomeRedirectChange: RouterOnChange = (prevState, nextState) => {
  const nextPath = nextState.location.pathname;
  if (nextPath.endsWith('/admin')) {
    if (AuthStore.getState().user.isAdmin()) {
      embeddedReplace('/admin/users');
    } else {
      embeddedReplace('/admin/profile');
    }
  }
};

/**
 * Determine if user has a valid dashboard url.
 *
 *   If no ids are supplied, then redirect
 *   If Admin is supplied the user must be that admin.
 *   If Admin is not supplied and manager is, then user must be that manager
 *   If neither are supplied and coach is, then user must be that coach
 *   otherwise the url is invalid
 */
function shouldRedirect({adminId, managerId, coachId}: DashUserParams = {}, authId: UserId) {
  if (!adminId && !managerId && !coachId) return true;
  if (adminId) return adminId !== authId;
  if (managerId) return managerId !== authId;
  if (coachId) return coachId !== authId;
  return false;
}

// This is used to know if the site is on it's first render, since we have specific "home page" logic that needs it.
// This is very, very dumb, and I am very, very sad about it.
let initialRender = true;

/**
 * Redirect user to their own summary when they reach somewhere they dont belong
 */
export const dashboardRedirectOnChange: RouterOnChange<DashUserParams> = (prevState, nextState) => {
  const {user} = AuthStore.getState();
  if (shouldRedirect(nextState.params, user.id)) {
    console.warn('User entered invalid Dashboard url', nextState);
    if (initialRender && hasFlag(featureFlags.newCoachHomepage, user)) {
      initialRender = false;
      embeddedReplace('/home');
    } else {
      embeddedReplace(getDashboardPath(user));
    }
  }
};

/**
 * Check if the user is authorized to enter the Admin page.
 */
export const adminCheck: RouterOnEnter = () => {
  const loggedInUser = AuthStore.getState().user;
  if (!loggedInUser?.isAdmin()) {
    redirectToHome();
  }
};

const isEmbed = (state: RouterState) => state.location.pathname.startsWith(embedPath);
/**
 * If we're already in the /embed route,
 * make sure we don't leave it when trying to navigate around the app
 */
export const embedNavCheck: RouterOnChange = (prevState, nextState) => {
  if (isEmbed(prevState!) && !isEmbed(nextState)) {
    browserHistory.replace({
      ...nextState.location,
      pathname: embedPath + nextState.location.pathname,
    });
  }
};
